"use client";

import React from "react";
import { Sparkles, Mail, Phone, MapPin, Linkedin, Instagram, Twitter } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
const services = [{
  label: 'M&A Advisory Services',
  href: '/MandAAdvisoryServices'
}, {
  label: 'Capital Raises',
  href: '/CapitalRaises'
}, {
  label: 'Strategic Advisory',
  href: '/StrategicAdvisory'
}];
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-24 bg-background/10 dark:bg-foreground/10 animate-pulse rounded-md" />)}
      </div>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-background/70 hover:text-primary dark:text-foreground/70 dark:hover:text-primary transition-colors text-sm font-medium">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  return <section data-section-id="232606" id="footer" className="bg-background">
      <div className="relative overflow-hidden bg-primary text-background dark:bg-background dark:text-foreground pt-20 pb-8 border-t border-primary-foreground/10">
        
        <Sparkles className="absolute top-12 left-[10%] text-primary-foreground/20 size-16 rotate-12" aria-hidden="true" />
        <Sparkles className="absolute bottom-32 right-[15%] text-primary-foreground/15 size-24 -rotate-12" aria-hidden="true" />
        <Sparkles className="absolute top-1/3 right-[30%] text-primary-foreground/10 size-10" aria-hidden="true" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-default text-3xl md:text-4xl lg:text-5xl text-background dark:text-foreground mb-6 leading-tight font-bold tracking-tight">
              Unlock Maximum Value. <br className="md:hidden" />
              <span className="text-primary-foreground">For Your Business Legacy.</span>
            </h2>
            <div className="h-1 w-20 bg-primary-foreground mx-auto opacity-50"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-8 mb-16">
            
            <div>
              <h3 className="font-default text-lg font-semibold mb-6 text-background dark:text-foreground uppercase tracking-widest">
                Quick Links
              </h3>
              <MenuProvider menu_id="83">
                <FooterMenuConsumer />
              </MenuProvider>
            </div>

            <div>
              <h3 className="font-default text-lg font-semibold mb-6 text-background dark:text-foreground uppercase tracking-widest">
                Advisory Services
              </h3>
              <ul className="space-y-3">
                {services.map((service, i) => <li key={i} data-index={i}>
                    <Link to={service.href} className="text-background/70 hover:text-primary-foreground dark:text-foreground/70 dark:hover:text-primary transition-colors text-sm font-medium flex items-center gap-2">
                      <span className="size-1.5 bg-primary-foreground/50"></span>
                      {service.label}
                    </Link>
                  </li>)}
              </ul>
            </div>

            <div>
              <h3 className="font-default text-lg font-semibold mb-6 text-background dark:text-foreground uppercase tracking-widest">
                Connect With Us
              </h3>
              
              <div className="space-y-6">
                <div className="space-y-3">
                  
                  <ul className="space-y-2">
                    <li>
                      <Link to="tel:+19496988293" className="flex items-center gap-3 text-background/70 hover:text-primary-foreground dark:text-foreground/70 dark:hover:text-primary transition-colors text-sm">
                        <Phone className="size-3.5" />
                        +1 (949) 698-8293
                      </Link>
                    </li>
                    <li>
                      <Link to="mailto:Jeff@BPVConsulting.com" className="flex items-center gap-3 text-background/70 hover:text-primary-foreground dark:text-foreground/70 dark:hover:text-primary transition-colors text-sm">
                        <Mail className="size-3.5" />
                        Jeff@BPVConsulting.com
                      </Link>
                    </li>
                    <li>
                      <Link to="https://linkedin.com" newTab className="flex items-center gap-3 text-background/70 hover:text-primary-foreground dark:text-foreground/70 dark:hover:text-primary transition-colors text-sm">
                        <Linkedin className="size-3.5" />
                        LinkedIn
                      </Link>
                    </li>
                    <li>
                      <Link to="https://instagram.com" newTab className="flex items-center gap-3 text-background/70 hover:text-primary-foreground dark:text-foreground/70 dark:hover:text-primary transition-colors text-sm">
                        <Instagram className="size-3.5" />
                        Instagram
                      </Link>
                    </li>
                    <li>
                      <Link to="https://x.com" newTab className="flex items-center gap-3 text-background/70 hover:text-primary-foreground dark:text-foreground/70 dark:hover:text-primary transition-colors text-sm">
                        <Twitter className="size-3.5" />
                        X (Twitter)
                      </Link>
                    </li>
                  </ul>
                </div>

                

                <div className="pt-2">
                  <div className="flex items-center gap-3 text-background/70 dark:text-foreground/70 text-sm">
                    <MapPin className="size-4" />
                    Newport Beach, California
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-background/10 dark:border-foreground/10 flex flex-col md:flex-row items-center justify-between gap-6">
            
            <div className="flex items-center gap-4 order-2 md:order-1 flex-col md:flex-row">
              <Link to="/">
                <WvcLogo className="h-7 w-auto opacity-90 hover:opacity-100 transition-opacity" />
              </Link>
              <div className="text-[10px] text-background/50 dark:text-foreground/50 uppercase tracking-widest space-y-2 max-w-2xl">
                <p>
                  © {new Date().getFullYear()} Blue Phoenix Ventures. All rights reserved.
                </p>
                <p>
                  Securities transactions are conducted through GT Securities, Inc., Member <Link to="https://www.finra.org/" newTab rel="noopener noreferrer" className="underline hover:text-primary-foreground transition-colors">FINRA</Link>/SIPC. Blue Phoenix Ventures and GT Securities, Inc. are separate but affiliated entities. Check the background of this firm on <Link to="https://brokercheck.finra.org/" newTab rel="noopener noreferrer" className="underline hover:text-primary-foreground transition-colors">FINRA's BrokerCheck</Link>.
                </p>
                <p>
                  <Link to="https://bpvconsulting.com/regulatory-disclosures/" className="underline hover:text-primary-foreground transition-colors">Regulatory Disclosures</Link>
                </p>
                <p>
                  Confidentiality Statement: All information shared with Blue Phoenix Ventures is confidential and will not be disclosed without consent.
                </p>
                <p>
                  Fee Protection: We confirm our willingness to pay full fees as agreed.
                </p>
                <p>
                  Response Guarantee: We will respond to all inquiries within five (5) business days.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-6 order-1 md:order-2">
              <div className="flex items-center gap-2 text-sm text-background/70 dark:text-foreground/70 bg-background/5 dark:bg-foreground/5 px-3 py-1.5">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1657590/img/usa_flag_icon.png" alt="USA Flag" className="h-3.5 w-5 object-cover shadow-sm" />
                <span className="font-medium font-default">Crafted in California</span>
              </div>

              <Select defaultValue="en">
                <SelectTrigger className="w-[80px] h-8 bg-transparent border-background/20 dark:border-foreground/20 text-background dark:text-foreground hover:bg-background/5 dark:hover:bg-foreground/5 focus:ring-primary-foreground/50">
                  <SelectValue placeholder="Lang" />
                </SelectTrigger>
                <SelectContent className="">
                  <SelectItem value="en">EN</SelectItem>
                  <SelectItem value="ar">AR</SelectItem>
                </SelectContent>
              </Select>
            </div>

          </div>
        </div>
      </div>
    </section>;
}